import React, { useCallback, useMemo, useState } from 'react'
import { RefreshCw, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { RecommendationsCard } from '../../../components/RecommendationsCard/RecommendationsCard'
import { PerformanceDashboardCard } from '../../../components/PerformanceDashboard'
import {
  demoResetSummary,
  type ActionSummaryData,
} from '../../../services/recommendations_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import useApiService from '../../../services/api'
import ReactSelectWrapper from '../../../components/FormUtils/ReactSelectWrapper'
import type { SingleValue } from 'react-select'
import { useHideFooter } from '../../../context/layout'
import { NewFooter } from '../../../components/Layout/Footer/NewFooter'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'
import { cn } from '../../../components/UI/cn'
import type { RightPanelState } from '../../../components/RecommendationsCard/RightPanel'
import confirm from '../../../components/dialogConfirm'

/**
 * Home page - Your daily recommendations and priorities
 *
 * Single centered column: greeting → performance → pipeline snapshot →
 * action center → manage-agents footer link. Width is constrained with
 * a max-width container so dashboards remain readable on wide displays.
 *
 * Superusers can select any user to view/generate their recommendations.
 */
export function HomePage() {
  useHideFooter()
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const queryClient = useQueryClient()
  const { user, isAdmin, isLimited } = useAuth()
  const { data: users } = useApiService().useGetUsers(
    (!user?.is_superuser && !isAdmin) || isLimited
  )

  const isSuperuser = user?.is_superuser
  const canManageSummaries = isSuperuser || isAdmin

  // Local-time greeting: morning < 12, afternoon < 18, else evening.
  // Computed once per render rather than memoized — Date() is cheap and
  // we don't want a stale greeting if the user keeps the tab open
  // across the noon/6pm boundary (a re-render close to that time will
  // pick up the new band naturally).
  const greeting =
    new Date().getHours() < 12
      ? 'Good morning!'
      : new Date().getHours() < 18
        ? 'Good afternoon!'
        : 'Good evening!'

  const summaryQueryKey = useMemo(
    () => ['recommendations', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const handleDemoReset = async () => {
    const targetLabel = selectedUserId
      ? (selectedUser?.email ?? 'this user')
      : (user?.email ?? 'your account')
    const confirmed = await confirm(
      <>
        This wipes all recommendations for <strong>{targetLabel}</strong> and
        replays the demo seed in their place. Any existing recommendation state
        (applied actions, dismissals, in-flight LLM evaluations) is lost. Run
        only against demo accounts.
      </>,
      'Reset demo data?',
      'This cannot be undone.',
      { confirmLabel: 'Reset demo data', buttonVariant: 'danger' }
    )
    if (!confirmed) return

    setIsGenerating(true)
    try {
      await demoResetSummary(selectedUserId)
      // Invalidate to force a fresh fetch — the reset is synchronous so
      // the DB already has the new summary.
      await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
    } catch (error) {
      console.error('Failed to reset demo:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      if (summary?.status === 'COMPLETED' || summary?.status === 'FAILED') {
        setIsGenerating(false)
      }
    },
    []
  )

  const userOptions = useMemo(() => {
    const others =
      users?.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })) ?? []
    // Superusers are excluded from the users/ endpoint — prepend self so they
    // can select their own summary from the dropdown
    if (user && !others.find((o) => o.value === user.id)) {
      others.unshift({
        value: user.id,
        label: `${user.first_name} ${user.last_name} (${user.email}) (me)`,
      })
    }
    return others
  }, [users, user])
  const selectedUser =
    users?.find((u) => u.id === selectedUserId) ??
    (selectedUserId === user?.id ? user : undefined)
  const selectedUserOption = userOptions.find(
    (option) => option.value === selectedUserId
  )

  // Single panel-at-a-time state. Setting a new ``kind`` replaces
  // whatever was open. RecommendationsCard reads this and portals the
  // matching content into the host ref below.
  const [activePanel, setActivePanel] = useState<RightPanelState>({
    kind: 'closed',
  })
  const isPanelOpen = activePanel.kind !== 'closed'
  const resolvedSiderHostRef = React.useRef<HTMLDivElement>(null)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 bg-surface-alt overflow-hidden relative">
        {/* Scrollable content layer. Constrained to the relative
            wrapper so the sider that overlays it (below) doesn't
            scroll with content. */}
        <div className="absolute inset-0 overflow-auto">
          <div className="max-w-7xl mx-auto py-4 px-6 lg:px-8">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <h1 className="text-3xl text-fg">{greeting}</h1>
                <p className="mt-1 text-lg text-fg-secondary">
                  Here&apos;s what needs your attention today
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isSuperuser && (
                  <button
                    type="button"
                    onClick={handleDemoReset}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-1.5 h-[38px] px-3 text-sm font-medium rounded-md border border-error text-error hover:bg-error-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title={
                      selectedUserId
                        ? `Reset demo data for ${selectedUser?.email}. This wipes their recommendations and seeds fresh fixtures.`
                        : 'Reset demo data — wipes recommendations and seeds fresh fixtures.'
                    }
                  >
                    <RefreshCw
                      size={14}
                      className={isGenerating ? 'animate-spin' : ''}
                    />
                    Reset User Demo Data
                  </button>
                )}
                {canManageSummaries && users && (
                  <ReactSelectWrapper
                    className="min-w-[200px] text-sm"
                    options={userOptions}
                    value={selectedUserOption ?? null}
                    onChange={(option: unknown) => {
                      const typedOption = option as SingleValue<{
                        label: string
                        value: number
                      }>
                      const id = typedOption?.value
                      setSelectedUserId(id === user?.id ? undefined : id)
                      setIsGenerating(false)
                    }}
                    placeholder="My Summary"
                    isClearable
                  />
                )}
              </div>
            </div>

            <div className="space-y-6">
              <PerformanceDashboardCard userId={selectedUserId} />

              <div className="bg-surface rounded-lg border border-line p-4">
                <ErrorBoundary
                  fallback={
                    <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                      <p className="text-red-800 font-medium">
                        Failed to load recommendations
                      </p>
                      <p className="mt-1 text-sm text-red-600">
                        Please try refreshing the page
                      </p>
                    </div>
                  }
                >
                  <RecommendationsCard
                    userId={selectedUserId}
                    onSummaryLoaded={handleSummaryLoaded}
                    activePanel={activePanel}
                    onActivePanelChange={setActivePanel}
                    resolvedSiderHostRef={resolvedSiderHostRef}
                    headerSlot={
                      <div className="flex items-center justify-between gap-2 pb-2">
                        <h2 className="text-3xl text-fg">Action Center</h2>
                        <Link
                          to="/settings/ai"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-fb-dark-green font-medium rounded-lg border border-line hover:bg-surface-alt transition-colors"
                        >
                          <Sparkles size={16} />
                          Manage AI Agents
                        </Link>
                      </div>
                    }
                  />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>

        {/* Dimming backdrop — sits between the scrollable content and
            the overlay sider. Click to close. Mounted only when the
            sider is open so it doesn't intercept clicks otherwise. */}
        {isPanelOpen && (
          <div
            className="absolute inset-0 bg-black/30 transition-opacity duration-300 z-10"
            onClick={() => setActivePanel({ kind: 'closed' })}
            aria-hidden="true"
          />
        )}

        {/* Recently-completed sider host. Overlays the right side of
            the content area (no flex-sibling squish). Always rendered
            so the portal target is available; width animates between
            0 and 945px (capped to viewport via ``max-w-full`` on
            narrower screens). Sits above the backdrop. */}
        <div
          ref={resolvedSiderHostRef}
          className={cn(
            'absolute top-0 right-0 bottom-0 overflow-hidden bg-surface transition-[width] duration-300 ease-out z-20 max-w-full',
            isPanelOpen
              ? 'w-[945px] border-l border-line shadow-2xl'
              : 'w-0 border-l-0'
          )}
        />
      </div>
      <NewFooter />
    </div>
  )
}
